import { apiService } from './api';
import type {
  EmailCopy, EmailLogEntry, EmailLogResponse, EmailPreview, EmailSettings, EmailSettingsPatch, EmailSenderKind, EmailStatus,
  MailboxInput, RecordEmails, UpcomingEmail,
} from '../types/email';

/** Wraps /api/email — the Email pages and the emails on each record. */
export const emailService = {
  getSettings: async (): Promise<EmailSettings> =>
    (await apiService.get<EmailSettings>('/email/settings')).data!,

  updateSettings: async (patch: EmailSettingsPatch): Promise<EmailSettings> =>
    (await apiService.patch<EmailSettings>('/email/settings', patch)).data!,

  /** Signs in and sends a test to the mailbox itself before saving it. */
  connectMailbox: async (input: MailboxInput): Promise<EmailSettings> =>
    (await apiService.put<EmailSettings>('/email/settings/mailbox', input)).data!,

  disconnectMailbox: async (): Promise<EmailSettings> =>
    (await apiService.delete<EmailSettings>('/email/settings/mailbox')).data!,

  sendTest: async (to: string): Promise<{ sender: EmailSenderKind; to: string }> =>
    (await apiService.post<{ sender: EmailSenderKind; to: string }>('/email/settings/test', { to })).data!,

  /** The email for a made-up applicant, in the saved wording or in `copy` while it's edited. */
  preview: async (type: string, copy?: EmailCopy): Promise<EmailPreview> =>
    (await apiService.post<EmailPreview>('/email/settings/preview', { type, copy })).data!,

  log: async (params: { status?: EmailStatus; type?: string; page?: number; limit?: number }): Promise<EmailLogResponse> =>
    (await apiService.get<EmailLogResponse>('/email/log', params)).data!,

  forRecord: async (entityType: string, entityId: string): Promise<RecordEmails> =>
    (await apiService.get<RecordEmails>(`/email/records/${entityType}/${entityId}`)).data!,

  /** Before staff act: does this email go out, and to whom, for each record. */
  upcoming: async (type: string, entityIds: string[]): Promise<UpcomingEmail> =>
    (await apiService.post<UpcomingEmail>('/email/upcoming', { type, entityIds })).data!,

  resend: async (logId: string): Promise<EmailLogEntry> =>
    (await apiService.post<EmailLogEntry>(`/email/log/${logId}/resend`)).data!,
};
